import { Show } from "shared/components";
import { CallRecordingPlayer } from "shared/components/CallRecordingPlayer";
import type { TranscriptLine } from "shared/components/CallTranscript";
import { VoicemailTranscriptText } from "shared/app/Voicemail/VoicemailTranscriptText";
import { voicemailTranscriptText } from "shared/app/Voicemail/voicemail";
import { useVoicemailPlayed } from "shared/hooks/use-voicemail-played";
import { MessageTranslateButton } from "./MessageTranslateButton";
import { useMessageTranslation } from "../hooks/useMessageTranslation";

/**
 * The voicemail inside an Inbox call card (CRM-1777): the player once the
 * recording's copy is ready, and the transcript — collapsed to three lines,
 * translatable like an incoming message. Playing it reports the voicemail as
 * heard for the whole team.
 */
export const VoicemailBody = ({
  callId,
  recordingUrl,
  durationSeconds,
  transcript,
  transcriptPreview,
  onPlayed
}: {
  callId: string;
  recordingUrl?: string | null;
  durationSeconds?: number | null;
  // Undefined until the transcript request answers.
  transcript?: TranscriptLine[];
  transcriptPreview?: string | null;
  onPlayed: () => void;
}) => {
  const reportPlayed = useVoicemailPlayed(callId, { onPlayed });
  // The preview stands in until the full transcript loads.
  const text = transcript
    ? voicemailTranscriptText(transcript)
    : (transcriptPreview ?? "");
  const translation = useMessageTranslation({
    text,
    direction: "inbound",
    enabled: text.length > 0
  });
  const shownText =
    translation.showingTranslation && translation.translatedText !== null
      ? translation.translatedText
      : text;

  return (
    <div className="space-y-3">
      <Show if={!!recordingUrl}>
        <CallRecordingPlayer
          recordingUrl={recordingUrl ?? ""}
          durationSeconds={durationSeconds}
          onPlay={reportPlayed}
        />
      </Show>
      <VoicemailTranscriptText
        text={shownText}
        trailing={<MessageTranslateButton translation={translation} />}
      />
    </div>
  );
};
